/**
 * Cloudflare Turnstile widget for email/password registration.
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './TurnstileWidget.css';

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let turnstileScriptPromise = null;

/**
 * Load the Turnstile script once.
 *
 * @returns {Promise<void>}
 */
const loadTurnstileScript = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('No window'));
  }
  if (window.turnstile?.render) {
    return Promise.resolve();
  }
  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }
  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src^="https://challenges.cloudflare.com/turnstile"]`);
    if (existing) {
      if (window.turnstile?.render) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => {
          turnstileScriptPromise = null;
          reject(new Error('Turnstile script failed'));
        },
        { once: true },
      );
      return;
    }
    const script = document.createElement('script');
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      turnstileScriptPromise = null;
      reject(new Error('Turnstile script failed'));
    };
    document.head.appendChild(script);
  });
  return turnstileScriptPromise;
};

/**
 * Managed Turnstile challenge. Hidden when siteKey is empty.
 *
 * @param {Object} props
 * @returns {JSX.Element|null}
 */
const TurnstileWidget = ({ siteKey, onToken, onExpire, onError, language = 'auto' }) => {
  const hostRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);

  onTokenRef.current = onToken;
  onExpireRef.current = onExpire;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!siteKey || !hostRef.current) {
      return undefined;
    }

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !hostRef.current || !window.turnstile?.render) {
          return;
        }
        if (widgetIdRef.current != null) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch (err) {
            // ignore
          }
          widgetIdRef.current = null;
        }
        hostRef.current.innerHTML = '';
        widgetIdRef.current = window.turnstile.render(hostRef.current, {
          sitekey: siteKey,
          theme: 'light',
          language,
          callback: (token) => {
            onTokenRef.current?.(token);
          },
          'expired-callback': () => {
            onExpireRef.current?.();
          },
          'error-callback': () => {
            onErrorRef.current?.();
          },
        });
      })
      .catch(() => {
        onErrorRef.current?.();
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile?.remove) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (err) {
          // ignore
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, language]);

  if (!siteKey) {
    return null;
  }

  return (
    <div className="turnstile-widget">
      <div ref={hostRef} className="turnstile-widget__host" />
    </div>
  );
};

TurnstileWidget.propTypes = {
  siteKey: PropTypes.string,
  onToken: PropTypes.func.isRequired,
  onExpire: PropTypes.func,
  onError: PropTypes.func,
  language: PropTypes.string,
};

TurnstileWidget.defaultProps = {
  siteKey: '',
  onExpire: undefined,
  onError: undefined,
  language: 'auto',
};

export default TurnstileWidget;
